import { ApolloError, useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import {
  ErrorBoundary,
  ListSelectionStyled,
  ProjectAppTypes,
  SuspenseLoader,
  useEnvProvider,
  WeldMilestoneType,
} from '@bechtel/client-common';
import { CREATE_FITUP_REQUEST, GET_CREW_MEMBERS, REASSIGN_FITUP_REQUEST } from '@bechtel/client-gql';
import {
  Box,
  Radio,
  SideSheetFooter,
  Spinner,
  Text,
  TextArea,
  TextFieldSize,
  ToastAction,
  ToastType,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  CreateFitupRequestsError,
  CreateFitupRequestsInput,
  CreateFitupRequestsPayload,
  CrewMemberList,
  FitupRequestCrewMember,
  MilestoneStatusCode,
  PagingInput,
  PersonaType,
  ReassignFitupRequestError,
  ReassignFitupRequestInput,
  ReassignFitupRequestPayload,
  SearchCrewMemberInput,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ErrorMessage, Field, Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import { find, get } from 'lodash';
import { FC, Suspense, useEffect, useState } from 'react';
import { object, string } from 'yup';
import { WeldCrewMemberSelector } from '~/components/molecules';
import { ConfirmClaimAction } from '~/components/molecules/confirm';
import { CreateFitupRequestErrorModal } from './error';
import { FooterButtonStyled, HeadingStyled } from './styled';
import { fitUpRequireWeldingScopes, WeldAssignFormikState, WeldAssignReassignProps } from './types';

const weldAssignValidationSchema = object().shape({
  comments: string()
    .min(2, t`Too Short!`)
    .max(2000, t`Too Long!`),
  installationCrewMember: object().required(t`Weld Installation Foreman is mandatory`),
});

export const WeldAssignReassign: FC<WeldAssignReassignProps> = ({
  isFitupRequired,
  weldIds,
  close,
  isReassign,
  fitupRequest,
  isFlangeSidesheet = true,
  weld,
  showItrCertDialog = false,
}) => {
  const { i18n } = useLingui();
  const { envConfig } = useEnvProvider();
  let form: FormikProps<WeldAssignFormikState>;
  const [createFitupRequestsError, setCreateFitupsRequestError] = useState<
    CreateFitupRequestsError | ApolloError | ReassignFitupRequestError | null
  >(null);
  const dispatchToast = useToastsDispatch();
  const client = useApolloClient();
  const installCommodityAssignee = find(weld?.commodityAssignees, (item) => {
    return item.personaType == PersonaType.FOREMAN;
  });

  // Optimized query to validate crew member - fetches only active foremen with smaller limit
  const [validateCrewMember] = useLazyQuery<
    { crewMembers: CrewMemberList },
    { input: SearchCrewMemberInput; paging: PagingInput }
  >(GET_CREW_MEMBERS, {
    fetchPolicy: 'network-only', // Always fetch fresh data to ensure we get current active status
  });

  const { visible: itrCertModalVisible, open: itrCertModalOpen, close: itrCertModalClose } = useModalState(false);
  const [formikValues, setFormikValues] = useState<WeldAssignFormikState>();
  const [formikActions, setFormikActions] = useState<FormikActions<WeldAssignFormikState>>();

  const initialValues: WeldAssignFormikState = {
    isFitupRequired: isFitupRequired,
    fitupRequiredWelding: fitupRequest?.isWeldingRequired ?? true,
    comments: fitupRequest?.comments ? fitupRequest.comments : '',
    installationCrewMember: fitupRequest?.installationCrewMember
      ? fitupRequest.installationCrewMember
      : installCommodityAssignee?.assignee,
  };

  const fitupMilestone = find(weld?.milestones, (item) => {
    return item.name.toUpperCase() === WeldMilestoneType.FITUP;
  });

  const fitupMilestoneStatus = fitupMilestone && fitupMilestone.currentStatus?.codeI18n;
  const showWeldingRequired =
    isFitupRequired && envConfig.appName === ProjectAppTypes.PF
      ? weld?.fitupRequest?.id
        ? weld?.fitupRequest?.isFitupRequired && fitupMilestoneStatus !== MilestoneStatusCode.APPROVED
        : true
      : false;

  const disableSubmitBtn = fitupMilestoneStatus && fitupMilestoneStatus !== MilestoneStatusCode.CANCELED;

  const createFitupRequestToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>Fitup request created successfully.</Trans>
        </Text>
      </Box>
    );
  };

  const reassignFitupRequestToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>Fitup request reassigned successfully.</Trans>
        </Text>
      </Box>
    );
  };

  useEffect(() => {
    if (!form) return;
    const { values, setValues } = form;
    if (!isFitupRequired && !fitupRequest?.isWeldingRequired) {
      setValues({ ...values, fitupRequiredWelding: false });
    }
  }, [isFitupRequired, fitupRequest?.isWeldingRequired]);

  const onFitupRequestComplete = (data: { createFitupRequests: CreateFitupRequestsPayload }) => {
    const createFitupRequestsError = get(data, 'createFitupRequests.erroredWelds', undefined);
    if (itrCertModalVisible) {
      itrCertModalClose();
    }
    if (createFitupRequestsError) {
      setCreateFitupsRequestError(createFitupRequestsError);
    } else {
      client.refetchQueries({
        include: ['commodityStatsSummary'],
      });
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: createFitupRequestToast,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
      if (!isFlangeSidesheet) close();
    }
  };

  const onFitupReassignComplete = (data: { reassignFitupRequest: ReassignFitupRequestPayload }) => {
    const reassignFitupRequestsError: ReassignFitupRequestError = get(
      data,
      'reassignFitupRequest.errorCode',
      undefined
    );
    if (itrCertModalVisible) {
      itrCertModalClose();
    }
    if (reassignFitupRequestsError) {
      setCreateFitupsRequestError(reassignFitupRequestsError);
    } else {
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: reassignFitupRequestToast,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
      if (!isFlangeSidesheet) close();
    }
  };

  const [createFitupRequests, { loading: mutationLoading }] = useMutation<
    { createFitupRequests: CreateFitupRequestsPayload },
    { input: CreateFitupRequestsInput }
  >(CREATE_FITUP_REQUEST, {
    onCompleted: onFitupRequestComplete,
    onError: (error) => {
      setCreateFitupsRequestError(error);
    },
  });

  const [reassignFitupRequest, { loading: reassignLoading }] = useMutation<
    { reassignFitupRequest: ReassignFitupRequestPayload },
    { input: ReassignFitupRequestInput }
  >(REASSIGN_FITUP_REQUEST, {
    onCompleted: onFitupReassignComplete,
    onError: (error) => {
      setCreateFitupsRequestError(error);
    },
  });

  const onCertifyClick = async () => {
    onSubmit(formikValues, formikActions, true);
  };

  const onSubmit = async (
    values: WeldAssignFormikState,
    actions: FormikActions<WeldAssignFormikState>,
    fromCertDialog: boolean = false
  ) => {
    // Validate that the selected crew member is not empty/null
    if (!values?.installationCrewMember?.id) {
      actions.setFieldError('installationCrewMember', t`Please select a valid Weld Foreman`);
      return;
    }

    // Validate that the selected crew member is still active
    // Use optimized query with crewMemberId filter to fetch only the specific person
    try {
      const { data: crewMemberValidation } = await validateCrewMember({
        variables: {
          input: {
            crewMemberId: values.installationCrewMember.id, // Filter by specific crew member ID
            persona: PersonaType.FOREMAN,
            appTypeId: ProjectAppTypes.AS,
          },
          paging: {
            cursor: '1',
            limit: 1, // Only need 1 result since we're filtering by ID
          },
        },
      });

      const activeCrewMembers = get(crewMemberValidation, 'crewMembers.items', []);
      
      // If the query returns the crew member, they exist and are active
      if (activeCrewMembers.length === 0) {
        actions.setFieldError(
          'installationCrewMember',
          t`The selected Weld Foreman is no longer available. Please select a different foreman.`
        );
        return;
      }
    } catch (error) {
      // If validation fails, show error
      actions.setFieldError('installationCrewMember', t`Unable to validate Weld Foreman. Please try again.`);
      return;
    }

    const installationCrewMember: FitupRequestCrewMember = {
      crewMemberId: values?.installationCrewMember?.id ? values.installationCrewMember.id : '',
      personaType: values?.installationCrewMember?.persona ? values?.installationCrewMember?.persona : PersonaType.NONE,
    };
    const createFitupRequestsInput: CreateFitupRequestsInput = {
      commodityIds: weldIds,
      isFitupRequired: isFitupRequired,
      isWeldingRequired: values?.fitupRequiredWelding ? values.fitupRequiredWelding : false,
      installationCrewMember: installationCrewMember,
      comments: values?.comments ? values?.comments : '',
      itrRequired: fromCertDialog,
    };

    if (isReassign && weld?.fitupRequest) {
      const { items } = weld?.milestonesActivityHistory || {};
      const latestItem = items?.reduce((latest, item) => {
        const itemDate = new Date(item.dateTime);
        if (!latest || itemDate > new Date(latest?.dateTime)) {
          return item;
        }
        return latest;
      }, null);

      const latestMilestoneStatusId = latestItem ? latestItem.id : '';
      await reassignFitupRequest({
        variables: {
          input: {
            commodityMilestoneStatusId: latestMilestoneStatusId,
            comments: values?.comments ? values?.comments : '',
            commodityId: weldIds[0],
            installationCrewMember: installationCrewMember,
            isWeldingRequired: values?.fitupRequiredWelding ?? true,
            itrRequired: fromCertDialog,
          },
        },
      });
    } else {
      await createFitupRequests({
        variables: {
          input: createFitupRequestsInput,
        },
      });
    }

    actions.resetForm({
      values: {
        ...initialValues,
      },
    });
  };

  return (
    <>
      {itrCertModalVisible && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ConfirmClaimAction
              onCertifyClick={onCertifyClick}
              isMutationInProgress={mutationLoading || reassignLoading}
              close={itrCertModalClose}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      <Formik
        validationSchema={weldAssignValidationSchema}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={async (values: WeldAssignFormikState, actions: FormikActions<WeldAssignFormikState>) => {
          if (showItrCertDialog) {
            setFormikValues(values);
            setFormikActions(actions);
            itrCertModalOpen();
          } else {
            onSubmit(values, actions);
          }
        }}
      >
        {(formikProps: FormikProps<WeldAssignFormikState>) => {
          form = formikProps;
          const { setFieldValue, values, isValid, dirty, handleSubmit } = formikProps;
          return (
            <>
              <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} style={{ overflowY: 'auto' }}>
                {showWeldingRequired && (
                  <>
                    <HeadingStyled>
                      <Text size="400" family="graphik" weight="600" color="grey.1000">
                        <Trans>Does Fitup Require Welding?</Trans>
                      </Text>
                    </HeadingStyled>
                    {fitUpRequireWeldingScopes.map((scope) => {
                      return (
                        <ListSelectionStyled
                          justifyContent={{ sm: 'space-between' }}
                          key={scope.label}
                          onClick={() => {
                            setFieldValue('fitupRequiredWelding', scope.value);
                          }}
                        >
                          <Box>
                            <Box mr={{ sm: '500' }}>
                              <Radio checked={values.fitupRequiredWelding === scope.value} readOnly />
                            </Box>
                            <Text color="grey.1000" size="400" weight="600">
                              {i18n._(scope.label)}
                            </Text>
                          </Box>
                        </ListSelectionStyled>
                      );
                    })}
                  </>
                )}
                <HeadingStyled>
                  <Text size="400" family="graphik" weight="600" color="grey.1000">
                    {isReassign ? t`Reassign Weld Foreman` : 'Assign Weld Foreman'}
                  </Text>
                </HeadingStyled>
                <Box pt={{ sm: '600' }} px={{ sm: '500' }} pb={{ sm: '500' }}>
                  <WeldCrewMemberSelector fieldName="installationCrewMember" formLabel={t`Weld Foreman`} />
                </Box>
                <HeadingStyled>
                  <Text size="400" family="graphik" weight="600" color="grey.1000">
                    <Trans>Comments (Optional)</Trans>
                  </Text>
                </HeadingStyled>
                <Field name="comments">
                  {({ field }) => {
                    return (
                      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                        <TextArea maxLength={2000} size={TextFieldSize.LARGE} rows="5" {...field} />
                      </Box>
                    );
                  }}
                </Field>
                <ErrorMessage name="comments">
                  {(msg) => {
                    return (
                      <Box p={{ sm: '500' }} pt={{ sm: '000' }}>
                        <Text size="300" color="bechtel.red">
                          {i18n._(msg)}
                        </Text>
                      </Box>
                    );
                  }}
                </ErrorMessage>
              </Box>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }}>
                  <Box mr={{ sm: '400' }}>
                    <FooterButtonStyled disabled={mutationLoading || reassignLoading} onClick={close}>
                      <Trans> Cancel</Trans>
                    </FooterButtonStyled>
                  </Box>
                  <Box>
                    <FooterButtonStyled
                      disabled={mutationLoading || reassignLoading || !isValid || (!dirty && !disableSubmitBtn)}
                      onClick={() => handleSubmit()}
                    >
                      {(mutationLoading || reassignLoading) && <Spinner />}
                      {!mutationLoading && !reassignLoading && t`Submit`}
                    </FooterButtonStyled>
                  </Box>
                </Box>
              </SideSheetFooter>
            </>
          );
        }}
      </Formik>
      {createFitupRequestsError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CreateFitupRequestErrorModal
              errors={createFitupRequestsError}
              close={() => {
                setCreateFitupsRequestError(null);
              }}
              isReassign={isReassign}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
